import { useCallback, useEffect, useRef, useState } from 'react';
import { readPersistedWidth, writePersistedWidth } from '@shared/lib/persistedWidth';

const KEYBOARD_STEP = 24;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/**
 * Drives a side panel's user-resize behaviour:
 *   - Initial width = persisted (when storageKey set) ?? defaultWidth.
 *   - Drag handlers compute new width from a horizontal pointer delta,
 *     clamped to [minWidth, maxWidth].
 *   - On drag end, persists the new width (when storageKey set).
 *   - Keyboard handlers move width by a fixed step / jump to min-max.
 *
 * The panel is right-anchored, so dragging the LEFT edge to the LEFT
 * grows the panel. The hook abstracts that direction so the caller's
 * handle component doesn't have to remember it.
 *
 * Returns:
 *   - width: current width in CSS pixels (Number).
 *   - onPointerDown: bind to the drag-handle.
 *   - onKeyDown: bind to the drag-handle (keyboard a11y).
 */
const useResizableWidth = ({ enabled, defaultWidth, minWidth, maxWidth, storageKey }) => {
  const [width, setWidth] = useState(() => {
    if (!enabled) return defaultWidth;
    const persisted = readPersistedWidth(storageKey);
    return persisted ? clamp(persisted, minWidth, maxWidth) : defaultWidth;
  });

  // Keep latest width in a ref so the pointermove closure can read it
  // without recreating handlers every render.
  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(0);
  const isDraggingRef = useRef(false);

  const persist = useCallback(
    next => {
      if (storageKey) writePersistedWidth(storageKey, next);
    },
    [storageKey]
  );

  const handlePointerMove = useCallback(
    event => {
      if (!isDraggingRef.current) return;
      // Anchor=right + handle on left edge: dragging LEFT (negative
      // deltaX) increases width.
      const deltaX = event.clientX - dragStartXRef.current;
      const next = clamp(dragStartWidthRef.current - deltaX, minWidth, maxWidth);
      setWidth(next);
    },
    [minWidth, maxWidth]
  );

  const handlePointerUp = useCallback(
    event => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      try {
        event.target.releasePointerCapture?.(event.pointerId);
      } catch (_) {
        // pointer was already released
      }
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      persist(widthRef.current);
    },
    [handlePointerMove, persist]
  );

  const onPointerDown = useCallback(
    event => {
      if (!enabled) return;
      isDraggingRef.current = true;
      dragStartXRef.current = event.clientX;
      dragStartWidthRef.current = widthRef.current;
      try {
        event.target.setPointerCapture?.(event.pointerId);
      } catch (_) {
        // pointer capture not supported — fall back to window listeners
      }
      // Disable text selection / show resize cursor globally during drag.
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ew-resize';
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerUp);
      event.preventDefault();
    },
    [enabled, handlePointerMove, handlePointerUp]
  );

  const onKeyDown = useCallback(
    event => {
      if (!enabled) return;
      let next = widthRef.current;
      switch (event.key) {
        case 'ArrowLeft':
          // anchor=right: left arrow grows the panel
          next = clamp(widthRef.current + KEYBOARD_STEP, minWidth, maxWidth);
          break;
        case 'ArrowRight':
          next = clamp(widthRef.current - KEYBOARD_STEP, minWidth, maxWidth);
          break;
        case 'Home':
          next = maxWidth;
          break;
        case 'End':
          next = minWidth;
          break;
        default:
          return;
      }
      event.preventDefault();
      setWidth(next);
      persist(next);
    },
    [enabled, minWidth, maxWidth, persist]
  );

  // Re-clamp if min/max change at runtime (e.g., the consumer hot-reloads
  // a wider/narrower bound). Keeps the persisted value within the new
  // window without surprising the user.
  useEffect(() => {
    if (!enabled) return;
    const next = clamp(widthRef.current, minWidth, maxWidth);
    if (next !== widthRef.current) setWidth(next);
  }, [enabled, minWidth, maxWidth]);

  // Cleanup global listeners if the panel unmounts mid-drag.
  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    },
    [handlePointerMove, handlePointerUp]
  );

  return { width, onPointerDown, onKeyDown };
};

export default useResizableWidth;
